/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useMemo, useState } from 'react';
import {
    arrayOf,
    bool,
    func,
    number,
    oneOf,
    shape,
    string,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useSelector } from 'react-redux';
import { getBrand } from '../../../pages/Account/state/ducks/Common/Common-Selectors';
import ProductFacetItem from './ProductFacetItem';
import brandColors from './brandColors';

const INITIAL_VISIBLE_COUNT = 4;

const useStyles = makeStyles(() => ({
    root: {
        borderBottom: '1px solid #D9D9D9',
        '&:last-child': {
            borderBottom: 'none',
        },
        '&.MuiAccordion-root:before': {
            display: 'none',
        },
        '&.MuiAccordion-root': {
            boxShadow: 'none',
            margin: 0,
        },
    },
    summary: {
        padding: 0,
        minHeight: 'unset',
        alignItems: 'flex-start',
        '&.MuiAccordionSummary-root.Mui-expanded': {
            minHeight: 'unset',
        },
        '& .MuiAccordionSummary-expandIcon': {
            color: '#000000',
            marginRight: 0,
            padding: '16px 0px',
            alignSelf: 'flex-start',
        },
        '& .MuiAccordionSummary-content': {
            margin: '24px 0',
            alignItems: 'flex-start',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: '24px 0 16px',
        },
    },
    summaryContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    summaryTitleRow: {
        minHeight: '24px',
        display: 'flex',
        alignItems: 'center',
    },
    summaryTitle: {
        fontSize: '16px',
        fontWeight: 600,
        lineHeight: '16px',
        color: '#000000',
    },
    summarySubtitle: {
        fontSize: '14px',
        fontWeight: 300,
        color: ({ summarySubtitleColor }) => summarySubtitleColor,
    },
    details: {
        padding: '0 0 16px',
        display: 'block',
    },
    items: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
    },
    item: {
        minHeight: '40px',
        paddingLeft: '12px',
        '& .MuiFormControlLabel-root': {
            margin: 0,
            width: '100%',
        },
        '& .MuiFormControlLabel-label': {
            fontSize: '16px',
            fontWeight: 300,
            lineHeight: '16px',
            color: '#000000',
        },
    },
    showMoreBtn: {
        marginTop: '8px',
        marginBottom: '12px',
        border: 'none',
        background: 'transparent',
        padding: '0 20px',
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '20px',
        letterSpacing: '0.56px',
        cursor: 'pointer',
        width: 'fit-content',
        color: '#000000',
        '& span': {
            borderBottom: '2px solid #000000',
            paddingBottom: '2px',
        },
    },
}));

const ProductFacetGroup = ({
    facet,
    selectedValues,
    onChange,
    itemType,
    className,
    loading,
}) => {
    const brand = useSelector(getBrand);
    const summarySubtitleColor = brandColors?.[brand?.domain]?.subTitle || '#2F2F2F';
    const classes = useStyles({ summarySubtitleColor });
    const [showAll, setShowAll] = useState(false);
    const [expanded, setExpanded] = useState(!!facet.displayOpen);

    const hasMore = facet.entries.length > INITIAL_VISIBLE_COUNT;
    const selectedInFacet = useMemo(() => (
        facet.entries.filter((entry) => selectedValues.includes(entry.value))
    ), [facet.entries, selectedValues]);
    const subtitle = useMemo(() => {
        if (!selectedInFacet.length) return '';
        return selectedInFacet.map((entry) => entry.name).join(', ');
    }, [selectedInFacet]);
    const visibleEntries = useMemo(() => (
        showAll ? facet.entries : facet.entries.slice(0, INITIAL_VISIBLE_COUNT)
    ), [showAll, facet.entries]);

    return (
        <Accordion
            className={`${classes.root} ${className || ''}`}
            expanded={expanded}
            onChange={(_event, isExpanded) => setExpanded(isExpanded)}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.summary}>
                <div className={classes.summaryContent}>
                    <div className={classes.summaryTitleRow}>
                        <Typography className={classes.summaryTitle}>{facet.name}</Typography>
                    </div>
                    {!expanded && !!subtitle && <Typography className={classes.summarySubtitle}>{subtitle}</Typography>}
                </div>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
                <div className={classes.items}>
                    {visibleEntries.map((entry) => (
                        <ProductFacetItem
                            key={entry.value}
                            className={classes.item}
                            entry={entry}
                            checked={selectedValues.includes(entry.value)}
                            onChange={(value, checked) => onChange(facet.name, value, checked, entry)}
                            type={itemType}
                            loading={loading}
                        />
                    ))}
                    {hasMore && (
                        <button
                            type="button"
                            className={classes.showMoreBtn}
                            onClick={() => setShowAll((prev) => !prev)}
                        >
                            <span>{showAll ? 'Show less' : 'Show more'}</span>
                        </button>
                    )}
                </div>
            </AccordionDetails>
        </Accordion>
    );
};

ProductFacetGroup.propTypes = {
    facet: shape({
        name: string.isRequired,
        displayOpen: bool,
        entries: arrayOf(shape({
            name: string.isRequired,
            value: string.isRequired,
            count: number,
            from: number,
            to: number,
            tooltip: string,
        })),
    }).isRequired,
    selectedValues: arrayOf(string),
    onChange: func,
    itemType: oneOf(['checkbox', 'radio']),
    className: string,
    loading: bool,
};

ProductFacetGroup.defaultProps = {
    selectedValues: [],
    onChange: () => {},
    itemType: 'checkbox',
    className: '',
    loading: false,
};

export default ProductFacetGroup;
